import { useMutation } from '@tanstack/react-query'
import { Upload } from 'lucide-react'
import { useRef, useState } from 'react'
import type { PracticeApi, PracticeImportOutcome } from '../api/practice'

export function PracticeUpload({ api, exam, onImported }: { api: PracticeApi; exam: string; onImported: () => void }) {
  const picker = useRef<HTMLInputElement>(null)
  const [over, setOver] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [checked, setChecked] = useState<PracticeImportOutcome | null>(null)
  const [selectionError, setSelectionError] = useState<string | null>(null)
  const upload = useMutation({
    mutationFn: ({ file, preview }: { file: File; preview: boolean }) => api.upload(exam, file, preview),
    onSuccess: (outcome) => {
      setChecked(outcome)
      if (outcome.accepted && !outcome.preview) onImported()
    },
  })
  function choose(files: FileList | null) {
    if (upload.isPending) return
    setChecked(null)
    setSelectionError(null)
    upload.reset()
    setFile(null)
    if (!files?.length) return
    if (files.length !== 1 || !files[0]!.name.toLowerCase().endsWith('.zip')) {
      setSelectionError('Put the Practice folders together in one ZIP, then upload that ZIP.')
      return
    }
    const chosen = files[0]!
    setFile(chosen)
    upload.mutate({ file: chosen, preview: true })
  }
  const imported = checked?.accepted && !checked.preview

  return <section aria-label="Upload Practice"
    onDragOver={(event) => { event.preventDefault(); setOver(true) }}
    onDragLeave={() => setOver(false)}
    onDrop={(event) => { event.preventDefault(); setOver(false); choose(event.dataTransfer.files) }}
    className={`rounded-lg border-2 border-dashed p-4 ${over ? 'border-green-800 bg-green-050' : 'border-ink-100'}`}>
    <div className="flex flex-wrap items-center gap-3">
      <button type="button" className={quiet} disabled={upload.isPending} onClick={() => picker.current?.click()}>
        <Upload aria-hidden size={16} /> Upload files
      </button>
      <input ref={picker} type="file" accept=".zip" disabled={upload.isPending} className="sr-only" aria-label="Practice archive"
        onChange={(event) => { choose(event.target.files); event.target.value = '' }} />
      <span className="text-muted">or drop a ZIP of Paper/Topic Code folders here</span>
    </div>
    {file && <p className="mt-3 text-muted">{file.name}</p>}
    {upload.isPending && <p role="status" className="mt-3 text-muted">{upload.variables?.preview ? 'Checking the ZIP...' : 'Importing the Questions...'}</p>}
    {(upload.error || selectionError) && <p role="alert" className="mt-3 text-red-700">{selectionError ?? upload.error?.message}</p>}
    {checked && <>
      {checked.groups.length > 0 && <div className="mt-3 overflow-x-auto">
        <table className="w-full text-left">
          <caption className="mb-2 text-left font-bold">{imported ? 'Imported Questions' : 'Archive contents'}</caption>
          <thead><tr>{['Paper', 'Topic Code', 'Topic', 'Questions'].map((heading) => <th key={heading} scope="col" className="border-b border-ink-100 p-2">{heading}</th>)}</tr></thead>
          <tbody>{checked.groups.map((group) => <tr key={`${group.componentCode}/${group.topicCode}`}>
            <td className="p-2">{group.paperName}</td><td className="p-2">{group.topicCode}</td>
            <td className="p-2">{group.topicName}</td><td className="p-2">{group.questionCount}</td>
          </tr>)}</tbody>
        </table>
      </div>}
      {checked.errors.length > 0 && <div role="alert" className="mt-3 text-red-700"><p>Nothing was imported.</p><ul className="list-inside list-disc">{checked.errors.map((error, index) => <li key={index}>{error}</li>)}</ul></div>}
      {(checked.excludedFiles?.length ?? 0) > 0 && <details className="mt-3 text-muted">
        <summary>{checked.excludedFiles?.length} authoring notes left out</summary>
        <ul className="mt-1 list-inside list-disc">{checked.excludedFiles?.map((name) => <li key={name}>{name}</li>)}</ul>
      </details>}
    </>}
    {imported && <p role="status" className="mt-3 text-green-800">All Questions in the ZIP have been imported.</p>}
    {checked?.accepted && !imported && <button type="button" disabled={upload.isPending} onClick={() => upload.mutate({ file: file!, preview: false })}
      className="mt-3 rounded-md bg-green-800 px-4 py-2 font-bold text-white disabled:opacity-55">Import Questions</button>}
  </section>
}

const quiet = 'inline-flex items-center gap-2 rounded-md border border-green-800 px-4 py-2 font-bold text-green-800 hover:bg-green-050 disabled:opacity-55'
